import React from 'react';
import { useNavigate } from 'react-router-dom';
import { RightOutlined } from '@ant-design/icons';

import { NEUTROS as T } from '../../../utils/micrositioTema';
import { clickInterno } from '../../../utils/navegacionSpa';
import { fixUrl } from '../../../services/api';
import { BarraDep, DEPS_EN_PORTADA } from './BloqueToro';

/**
 * El bloque de UN toro en la plantilla OP-2.
 *
 * LA DIFERENCIA CON OP-1 ES LA DISPOSICIÓN, NO EL CONTENIDO. Los dos muestran
 * el mismo toro con los mismos datos y los mismos cuatro DEPs; lo que cambia es
 * cómo se apoyan en la página:
 *
 *   OP-1   foto a la izquierda, ficha al lado, dos secundarias debajo de la
 *          ficha y la descripción cerrando el bloque a lo ancho.
 *   OP-2   la foto ES EL FONDO del bloque entero, y una tarjeta BLANCA angosta
 *          flota sobre ella contra el borde derecho, con todo adentro.
 *
 * La diferencia no es de grados: en OP-1 la foto es la mitad del argumento; en
 * OP-2 es el argumento entero y los datos se leen sobre el animal.
 *
 * Por eso `BarraDep` y `DEPS_EN_PORTADA` se IMPORTAN de `BloqueToro` en vez de
 * copiarse: la barra de percentil es un instrumento de medición —1 es el mejor,
 * 100 el peor, y la barra se llena con `100 - percentil`— y tiene que leerse
 * idéntica en las dos plantillas. Dos copias es la forma más segura de que una
 * se desincronice de la otra.
 *
 * NO decide qué páginas existen. La plantilla cambia cómo se ve el index y nada
 * más: el "ver todos", la ficha y el listado son los mismos en las dos.
 *
 * @param {ReactNode} pie  Lo que va DEBAJO de la tarjeta, alineado a su borde:
 *   hoy, el botón "Ver todos los toros" de la sección. Lo arma `SeccionAnimales`
 *   y lo pasa hecho, porque es de la SECCIÓN y no del toro —navega al listado
 *   completo, sabe si hay más que mostrar— y este bloque no tiene por qué
 *   saber nada de eso. En OP-1 el mismo botón va al lado del título; en OP-2
 *   la cabecera flota arriba a la izquierda y la tarjeta abajo a la derecha, y
 *   un botón al lado del título quedaba a media pantalla de lo que amplía.
 */
const BloqueToroOp2 = ({ animal, config, tipografia, slug, pie = null }) => {
  const navigate = useNavigate();
  const acento = config.color_primary || '#1a5f2a';
  const colorTitulo = config.color_title || '#1a1a1a';

  // Los MISMOS cuatro que OP-1, con el mismo criterio: los mejores de ESTE
  // animal y no los primeros de la lista. El spread no es adorno — `sort` muta,
  // y mutar `animal.deps` sería escribir sobre las props.
  const deps = [...(animal.deps || [])]
    .filter((d) => d.percentil != null)
    .sort((a, b) => a.percentil - b.percentil)
    .slice(0, DEPS_EN_PORTADA);

  const hayDescripcion = Boolean((animal.descripcion || '').trim());
  const rutaFicha = `/genetica/${slug}/animal/${animal.slug}`;

  const verFicha = (
    <a
      href={rutaFicha}
      // Se intercepta el click simple para RUTEAR en vez de recargar la app
      // entera; el `href` sigue sirviendo a ctrl+click, al botón del medio y a
      // los buscadores. Ver `navegacionSpa.js`.
      onClick={clickInterno(navigate, rutaFicha)}
      style={{
        flex: 'none', fontSize: 12.5, fontWeight: 700, color: acento,
        display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
      }}
    >
      Ver ficha <RightOutlined style={{ fontSize: 10 }} />
    </a>
  );

  // LA URL VA ENTRE COMILLAS Y ENCODEADA, aunque hoy no haga falta: los nombres
  // pasan por `secure_filename` de werkzeug al subirse
  // (`imagen_routes.py`), que ya deja rutas limpias —"389M_1782707182891_web.jpg"—
  // sin espacios ni parentesis. Es un cinturon, no un parche a un bug vivo.
  //
  // Vale ponerlo igual porque el modo de falla es silencioso: un `url()`
  // invalido no da error en consola, el navegador descarta la declaracion
  // entera y el bloque queda con el fondo gris pelado.
  // EL PUNTO FOCAL decide QUE PARTE de la foto sobrevive al recorte. OP-2 la
  // muestra a ancho completo y la corta arriba y abajo: con el recorte centrado
  // —que es el default, 50/50— un animal que no esta en el medio de la toma
  // pierde la cabeza. El productor mueve el foco y la misma foto entra bien,
  // sin tener que sacar otra.
  //
  // Viaja SIEMPRE desde el backend, asi que el `|| 50` es para el catalogo
  // servido por una version vieja de la API, no para el caso normal.
  const foco = animal.imagen_foco || {};
  const fondoFoto = animal.imagen
    ? {
      backgroundImage: `url("${encodeURI(fixUrl(animal.imagen))}")`,
      backgroundPosition: `${foco.x ?? 50}% ${foco.y ?? 50}%`,
    }
    : undefined;

  return (
    <div className={`pj-op2-toro${animal.imagen ? '' : ' pj-op2-sin-foto'}`}>
      {/* LA FOTO ES EL FONDO, y va como background-image de CSS y NO como un
          <Image> de antd. La diferencia es el visor: en esta plantilla la foto
          NO se amplia desde el index, por decision del usuario, y tiene sentido
          — una lupa sobre un fondo invita a un gesto que no lleva a ningun
          lado, y el que quiere ver al animal de cerca tiene la ficha a un
          click, con todas sus fotos y sus flechas.

          Prescindir del visor destraba dos cosas: el full-bleed sale sin
          envoltorios, y el encuadre queda a un `background-position` — que es
          por donde va a entrar el punto focal por imagen si hace falta. */}
      {animal.imagen && (
        <div
          className="pj-op2-foto"
          style={fondoFoto}
          // La foto es DECORATIVA acá: el nombre del animal ya está escrito en
          // la tarjeta de al lado, así que un alt repetiría lo mismo para quien
          // usa lector de pantalla. Por eso va como fondo y no como <img>.
          role="presentation"
        />
      )}

      {/* LA TARJETA, flotando sobre la foto contra el borde derecho. Es un 20%
          más angosta que la ficha de OP-1 y por una razón: acá tapa al animal,
          y lo que se vende es el animal.

          El envoltorio `pj-op2-interior` respeta el MISMO ancho útil y el mismo
          padding que la cabecera de arriba, así el título y la tarjeta caen
          sobre el mismo eje aunque la foto llegue al borde de la pantalla.

          `pj-op2-columna` apila la tarjeta y el `pie` (el "Ver todos") con el
          mismo ancho, así el botón queda alineado al borde de la tarjeta y no
          al de la pantalla. */}
      <div className="pj-op2-interior">
      <div className="pj-op2-columna">
      <div className="pj-op2-card">
        <div
          style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'flex-start', gap: 10,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 19, fontWeight: 800, letterSpacing: '-.005em',
                lineHeight: 1.2, fontFamily: tipografia.title, color: colorTitulo,
              }}
            >
              {/* El nombre CORTO manda: es con el que se conoce al animal. El
                  backend ya cae a `nom_animal` si el productor no lo cargó. */}
              {animal.nombre_corto || animal.nom_animal || animal.rp_animal || 'Sin nombre'}
            </div>
            {/* El de pedigrí completo, chico y abajo. No es relleno: es el
                nombre con el que figura en la asociación, y un comprador que va
                a cruzar datos lo necesita. Sólo si es DISTINTO del corto. */}
            {animal.nombre_corto && animal.nom_animal !== animal.nombre_corto && (
              <div style={{ fontSize: 11, color: T.textDim, marginTop: 3 }}>
                {animal.nom_animal}
              </div>
            )}
            <div style={{ fontSize: 12, color: T.textDim, marginTop: 4, fontWeight: 500 }}>
              {[
                animal.nom_categoria,
                animal.rp_animal ? `RP ${animal.rp_animal}` : null,
                animal.cabana,
              ].filter(Boolean).join(' · ')}
            </div>
          </div>

          {/* El MISMO chip de raza que la ficha y que OP-1: radio 4, sin borde,
              fondo tenue del acento y sin mayúsculas. */}
          {/* EL MISMO CHIP QUE OP-1, `AnimalCard` y la ficha: <span>, radio 4,
              sin borde, fondo tenue del acento y sin mayusculas. Y el mismo
              ELEMENTO tambien, no solo los mismos estilos — el <Tag> de antd
              trae su propio alto, line-height y font-size del tema, asi que con
              las mismas reglas escritas igual no coincidiria. */}
          {animal.nom_raza && (
            <span
              style={{
                flex: 'none', fontSize: 11.5, fontWeight: 700, letterSpacing: '.03em',
                background: `${acento}14`, color: acento, borderRadius: 4,
                padding: '2px 8px', whiteSpace: 'nowrap',
              }}
            >
              {animal.nom_raza}
            </span>
          )}
        </div>

        {deps.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div
              style={{
                fontSize: 10.5, fontWeight: 700, letterSpacing: '.05em',
                textTransform: 'uppercase', color: T.textDim, marginBottom: 8,
              }}
            >
              DEPs · percentil en la raza
            </div>
            {/* `compacta`: la tarjeta de OP-2 es mas fina que la ficha de OP-1,
                asi que se achican la barra y la etiqueta. Los numeros —el valor
                del DEP y el rank— quedan igual: son el dato.

                El comentario va ACA y no adentro del map: en el cuerpo de una
                arrow que devuelve JSX directo, el comentario de llaves es
                sintaxis de HIJOS y rompe el parseo. */}
            {deps.map((dep) => (
              <BarraDep key={dep.clave} dep={dep} color={acento} compacta />
            ))}
          </div>
        )}

        {/* LA DESCRIPCIÓN VA ADENTRO DE LA TARJETA, que es la diferencia con
            OP-1: allá cierra el bloque a lo ancho, acá la tarjeta se la come.
            Recortada a tres líneas por el mismo motivo de siempre — es texto
            libre y nada impide que alguien escriba diez. */}
        {hayDescripcion && (
          <p
            style={{
              margin: '16px 0 0',
              fontFamily: tipografia.body, fontSize: 13, lineHeight: 1.6,
              color: config.color_paragraph || '#666',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {animal.descripcion}
          </p>
        )}

        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 10, marginTop: 16, paddingTop: 14,
            borderTop: `1px solid ${T.border}`,
          }}
        >
          <span style={{ fontSize: 11, fontWeight: 700, color: T.textDim }}>
            {animal.apto_comercializar ? (
              <span style={{ color: T.success }}>● Disponible</span>
            ) : (
              'En el plantel'
            )}
          </span>
          {verFicha}
        </div>
      </div>
      {/* Debajo de la tarjeta, no adentro: el botón es de la sección y la
          tarjeta es del toro. Adentro parecería que "Ver todos" amplía ESTE
          animal, que es lo que ya hace "Ver ficha". */}
      {pie && <div style={{ marginTop: 14 }}>{pie}</div>}
      </div>
      </div>
    </div>
  );
};

export default BloqueToroOp2;
